import { initializeApp } from "firebase/app";
import {
  getAuth,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  updatePassword,
  sendPasswordResetEmail,
  EmailAuthProvider,
  reauthenticateWithCredential
} from "firebase/auth";
import { getFirestore, doc, setDoc, getDoc, collection, getDocs, query, where, addDoc, updateDoc, orderBy, limit as firestoreLimit, startAfter, QueryDocumentSnapshot, deleteDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { UserProfile, Notification, Resource, Event, Ticket, TicketAttendee, Item, NewsArticle } from './types';
import { generateUniqueSlug } from './utils/slugUtils';

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: "AIzaSyCId3o96B6e2PnxbkXQ6jm9kG8RfQ6vC3Y",
  authDomain: "wattlenest.firebaseapp.com",
  projectId: "wattlenest",
  storageBucket: "wattlenest.firebasestorage.app",
  messagingSenderId: "828344472112",
  appId: "1:828344472112:web:193fb8e4dcccf6b00f13f5",
  measurementId: "G-W9CPHHJ1PK",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const functions = getFunctions(app);

// ActionCodeSettings for email link sign-in
const actionCodeSettings = {
  // URL you want to redirect back to. The domain (www.example.com) for this
  // URL must be in the authorized domains list in the Firebase Console.
  url: window.location.origin + "/login/complete",
  // This must be true.
  handleCodeInApp: true,
  // Mobile app configuration
  iOS: {
    bundleId: 'com.wattlenest.app'
  },
  android: {
    packageName: 'com.wattlenest.app',
    installApp: true,
    minimumVersion: '12'
  },
  // Gmail requires this format to show a "Continue" button instead of a raw link
  dynamicLinkDomain: 'wattlenest.page.link'
};

// Function to send sign-in email link
export const sendSignInLink = async (email: string): Promise<void> => {
  try {
    // For mobile apps, we create a Firebase Dynamic Link
    // This ensures the link opens properly on mobile devices
    // Include email as a URL parameter to help with the authentication flow
    const signInUrl = `${window.location.origin}/login/complete?email=${encodeURIComponent(email)}`;
    
    // Generate a Dynamic Link if we're on a production domain
    // This helps with email clients like Gmail that try to open links in their own webview
    let finalUrl = signInUrl;
    
    // Only use dynamic links in production
    if (window.location.hostname !== 'localhost') {
      // Generate a Firebase Dynamic Link
      const dynamicLinkApiKey = firebaseConfig.apiKey;
      const dynamicLinkDomain = 'wattlenest.page.link'; // Replace with your dynamic link domain
      
      // Construct the dynamic link URL
      const dynamicLinkApiUrl = `https://firebasedynamiclinks.googleapis.com/v1/shortLinks?key=${dynamicLinkApiKey}`;
      
      try {
        const response = await fetch(dynamicLinkApiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            dynamicLinkInfo: {
              domainUriPrefix: `https://${dynamicLinkDomain}`,
              link: signInUrl,
              androidInfo: {
                androidPackageName: 'com.wattlenest.app',
                androidFallbackLink: signInUrl
              },
              iosInfo: {
                iosBundleId: 'com.wattlenest.app',
                iosFallbackLink: signInUrl
              },
              navigationInfo: {
                enableForcedRedirect: true,
              }
            },
            suffix: {
              option: 'SHORT'
            }
          })
        });
        
        const data = await response.json();
        if (data.shortLink) {
          finalUrl = data.shortLink;
          console.log('Using dynamic link for authentication:', finalUrl);
        }
      } catch (error) {
        console.error('Error creating dynamic link:', error);
        // Fall back to regular link if dynamic link creation fails
      }
    }
    
    // Update the actionCodeSettings with the final URL
    const finalActionCodeSettings = {
      ...actionCodeSettings,
      url: finalUrl
    };
    
    await sendSignInLinkToEmail(auth, email, finalActionCodeSettings);
    
    // Save the email locally so you don't need to ask the user for it again
    // if they open the link on the same device.
    window.localStorage.setItem("emailForSignIn", email);
    return Promise.resolve();
  } catch (error) {
    return Promise.reject(error);
  }
};

// Function to check if the current URL is a sign-in link
export const checkSignInLink = (url: string): boolean => {
  return isSignInWithEmailLink(auth, url);
};

// Function to complete sign-in with email link
export const completeSignInWithEmailLink = async (
  email: string,
  url: string
): Promise<any> => {
  try {
    const result = await signInWithEmailLink(auth, email, url);
    // Clear email from storage
    window.localStorage.removeItem("emailForSignIn");
    return result;
  } catch (error) {
    return Promise.reject(error);
  }
};

// User Profile Functions

// Check if user profile exists
export const checkUserProfile = async (userId: string): Promise<boolean> => {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    return userDoc.exists() && userDoc.data().displayName;
  } catch (error) {
    console.error("Error checking user profile:", error);
    return false;
  }
};

// Create or update user profile
export const saveUserProfile = async (
  userId: string,
  profileData: any
): Promise<void> => {
  try {
    await setDoc(doc(db, "users", userId), profileData, { merge: true });
    
    // Call submitApplication function to notify about new user registration
    const submitApplicationFunction = httpsCallable(functions, 'submitApplication');
    await submitApplicationFunction();
  } catch (error) {
    console.error("Error saving user profile:", error);
    throw error;
  }
};

// Get user profile
export const getUserProfile = async (userId: string): Promise<UserProfile> => {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    if (userDoc.exists()) {
      return userDoc.data() as UserProfile;
    }
    throw new Error('User not found');
  } catch (error) {
    console.error("Error getting user profile:", error);
    throw error;
  }
};

// Upload profile image
export const uploadProfileImage = async (
  userId: string,
  file: File
): Promise<string> => {
  try {
    const storageRef = ref(storage, `profile-images/${userId}/${file.name}`);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    console.error("Error uploading profile image:", error);
    throw error;
  }
};

// Upload sponsor logo
export const uploadSponsorLogo = async (
  userId: string,
  file: File
): Promise<string> => {
  try {
    const storageRef = ref(storage, `sponsor-logos/${userId}/${file.name}`);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    console.error("Error uploading sponsor logo:", error);
    throw error;
  }
};

// Admin Functions

// Check if a user is an admin
export const checkIsAdmin = async (userId: string): Promise<boolean> => {
  try {
    const adminDoc = await getDoc(doc(db, "admins", userId));
    return adminDoc.exists();
  } catch (error) {
    console.error("Error checking admin status:", error);
    return false;
  }
};

// Check if a user is approved
export const checkIsApproved = async (userId: string): Promise<boolean> => {
  try {
    // Check if a document with the user's UID exists in the 'approved' collection
    const approvedDocRef = doc(db, "approved", userId);
    const approvedDocSnap = await getDoc(approvedDocRef);
    return approvedDocSnap.exists();
  } catch (error) {
    console.error("Error checking approval status:", error);
    return false;
  }
};

// Get all users for admin panel
export const getAllUsers = async (): Promise<Array<UserProfile & { isApproved: boolean }>> => {
  try {
    // Get all users
    const usersQuery = query(collection(db, "users"));
    const usersSnapshot = await getDocs(usersQuery);
    
    // Get all approved user IDs
    const approvedRef = collection(db, "approved");
    const approvedSnapshot = await getDocs(approvedRef);
    
    // Create a Set of approved user IDs for O(1) lookup
    const approvedUserIds = new Set<string>();
    approvedSnapshot.forEach((doc) => {
      approvedUserIds.add(doc.id);
    });
    
    // Map user data and add the isApproved flag based on presence in approvedUserIds
    const users = usersSnapshot.docs.map((docSnapshot: QueryDocumentSnapshot) => {
      const userData = docSnapshot.data() as UserProfile;
      const uid = docSnapshot.id;
      
      return {
        ...userData,
        uid,
        isApproved: approvedUserIds.has(uid)
      };
    });
    
    return users;
  } catch (error) {
    console.error("Error getting all users:", error);
    throw error;
  }
};

// Approve a user
export const approveUser = async (userId: string): Promise<void> => {
  try {
    // Add the user to the approved collection
    // The document ID is the user's UID
    const approvedDocRef = doc(db, "approved", userId);
    await setDoc(approvedDocRef, {
      approvedAt: new Date()
    });
  } catch (error) {
    console.error("Error approving user:", error);
    throw error;
  }
};

// Unapprove a user
export const unapproveUser = async (userId: string): Promise<void> => {
  try {
    // Remove the user from the approved collection
    const approvedDocRef = doc(db, "approved", userId);
    await deleteDoc(approvedDocRef);
  } catch (error) {
    console.error("Error unapproving user:", error);
    throw error;
  }
};

// Authentication functions
export const login = (email: string, password: string) => {
  return signInWithEmailAndPassword(auth, email, password);
};

export const register = async (email: string, password: string, userData: Partial<UserProfile>) => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  await createUserProfile(userCredential.user.uid, {
    ...userData,
    email,
    uid: userCredential.user.uid,
  });
  return userCredential;
};

export const logout = () => {
  return signOut(auth);
};

export const getCurrentUser = () => {
  return auth.currentUser;
};

export const onAuthChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// User profile functions
export const createUserProfile = async (userId: string, data: Partial<UserProfile>) => {
  const userDocRef = doc(db, 'users', userId);
  await setDoc(userDocRef, {
    ...data,
    createdAt: new Date(),
    updatedAt: new Date()
  });
};

export const updateUserProfile = async (userId: string, data: Partial<UserProfile>) => {
  const userDocRef = doc(db, 'users', userId);
  await updateDoc(userDocRef, {
    ...data,
    updatedAt: new Date()
  });
};

// Export auth, db, and storage
export { auth, db, storage };

// Notification Functions

// Create a new notification (admin only)
export const createNotification = async (title: string, message: string, createdBy: string): Promise<string> => {
  try {
    const notificationData: Omit<Notification, 'id'> = {
      title,
      message,
      createdAt: Date.now(),
      createdBy,
      deleted: false
    };
    
    const docRef = await addDoc(collection(db, "notifications"), notificationData);
    return docRef.id;
  } catch (error) {
    console.error("Error creating notification:", error);
    throw error;
  }
};

// Get notifications with pagination (10 at a time)
export const getNotifications = async (
  lastVisible?: QueryDocumentSnapshot
): Promise<{ notifications: Notification[]; lastVisible: QueryDocumentSnapshot | null }> => {
  try {
    let notificationsQuery;
    
    // Temporary solution until the index is created
    if (lastVisible) {
      notificationsQuery = query(
        collection(db, "notifications"),
        orderBy("createdAt", "desc"),
        startAfter(lastVisible),
        firestoreLimit(10)
      );
    } else {
      notificationsQuery = query(
        collection(db, "notifications"),
        orderBy("createdAt", "desc"),
        firestoreLimit(10)
      );
    }
    
    const querySnapshot = await getDocs(notificationsQuery);
    const lastVisibleDoc = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
    
    // Filter deleted notifications manually in code rather than in the query
    const notifications = querySnapshot.docs
      .map((doc: QueryDocumentSnapshot) => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter((notif: any) => !(notif as any).deleted) as Notification[];
    
    return {
      notifications,
      lastVisible: lastVisibleDoc
    };
  } catch (error) {
    console.error("Error getting notifications:", error);
    throw error;
  }
};

// Delete a notification (admin only)
export const deleteNotification = async (notificationId: string): Promise<void> => {
  try {
    const notificationRef = doc(db, "notifications", notificationId);
    await updateDoc(notificationRef, {
      deleted: true
    });
  } catch (error) {
    console.error("Error deleting notification:", error);
    throw error;
  }
};

// Get all notifications for admin panel
export const getAllNotifications = async (): Promise<Notification[]> => {
  try {
    // Temporary solution until the index is created
    const notificationsQuery = query(
      collection(db, "notifications"),
      orderBy("createdAt", "desc")
    );
    
    const querySnapshot = await getDocs(notificationsQuery);
    
    // Filter deleted notifications manually in code
    const notifications = querySnapshot.docs
      .map((doc: QueryDocumentSnapshot) => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter((notif: any) => !(notif as any).deleted) as Notification[];
    
    return notifications;
  } catch (error) {
    console.error("Error getting all notifications:", error);
    throw error;
  }
};

// Resource Functions

// Create a new resource (admin only)
export const createResource = async (
  title: string,
  body: string,
  imageFile: File | null,
  createdBy: string,
  published: boolean = false
): Promise<string> => {
  try {
    let imageURL = '';
    
    // Upload image if provided
    if (imageFile) {
      const storageRef = ref(storage, `resources/${Date.now()}_${imageFile.name}`);
      await uploadBytes(storageRef, imageFile);
      imageURL = await getDownloadURL(storageRef);
    }
    
    const resourceData: Omit<Resource, 'id'> = {
      title,
      body,
      imageURL,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      createdBy,
      published
    };
    
    const docRef = await addDoc(collection(db, "resources"), resourceData);
    return docRef.id;
  } catch (error) {
    console.error("Error creating resource:", error);
    throw error;
  }
};

// Update an existing resource (admin only)
export const updateResource = async (
  resourceId: string,
  data: Partial<Resource>,
  imageFile?: File | null
): Promise<void> => {
  try {
    const resourceRef = doc(db, "resources", resourceId);
    const updateData: any = {
      ...data,
      updatedAt: Date.now()
    };
    
    // Upload new image if provided
    if (imageFile) {
      const storageRef = ref(storage, `resources/${Date.now()}_${imageFile.name}`);
      await uploadBytes(storageRef, imageFile);
      updateData.imageURL = await getDownloadURL(storageRef);
    }
    
    await updateDoc(resourceRef, updateData);
  } catch (error) {
    console.error("Error updating resource:", error);
    throw error;
  }
};

// Get a specific resource
export const getResource = async (resourceId: string): Promise<Resource> => {
  try {
    const resourceDoc = await getDoc(doc(db, "resources", resourceId));
    if (resourceDoc.exists()) {
      return {
        id: resourceDoc.id,
        ...resourceDoc.data()
      } as Resource;
    }
    throw new Error('Resource not found');
  } catch (error) {
    console.error("Error getting resource:", error);
    throw error;
  }
};

// Get all resources (admin)
export const getAllResources = async (): Promise<Resource[]> => {
  try {
    const resourcesQuery = query(
      collection(db, "resources"),
      orderBy("createdAt", "desc")
    );
    
    const querySnapshot = await getDocs(resourcesQuery);
    
    const resources = querySnapshot.docs.map((doc: QueryDocumentSnapshot) => ({
      id: doc.id,
      ...doc.data()
    })) as Resource[];
    
    return resources;
  } catch (error) {
    console.error("Error getting all resources:", error);
    throw error;
  }
};

// Get published resources (for users)
export const getPublishedResources = async (): Promise<Resource[]> => {
  try {
    // Use a simpler query that doesn't require a composite index
    const resourcesQuery = query(
      collection(db, "resources"),
      orderBy("createdAt", "desc")
    );
    
    const querySnapshot = await getDocs(resourcesQuery);
    
    // Filter for published resources on the client side
    const resources = querySnapshot.docs
      .map((doc: QueryDocumentSnapshot) => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter((resource: any) => resource.published === true) as Resource[];
    
    return resources;
  } catch (error) {
    console.error("Error getting published resources:", error);
    throw error;
  }
};

// Delete a resource (admin only)
export const deleteResource = async (resourceId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, "resources", resourceId));
  } catch (error) {
    console.error("Error deleting resource:", error);
    throw error;
  }
};

// News Article Functions

export const createNewsArticle = async (
  title: string,
  body: string,
  imageFile: File | null,
  createdBy: string,
  published: boolean = false
): Promise<string> => {
  try {
    let imageURL = '';
    
    // Upload image if provided
    if (imageFile) {
      const storageRef = ref(storage, `news-images/${Date.now()}_${imageFile.name}`);
      await uploadBytes(storageRef, imageFile);
      imageURL = await getDownloadURL(storageRef);
    }
    
    // Get existing slugs to ensure uniqueness
    const existingArticles = await getAllNewsArticles();
    const existingSlugs = existingArticles.map(article => article.slug || '');
    const slug = generateUniqueSlug(title, existingSlugs);
    
    const now = Date.now();
    
    const newArticle = {
      title,
      slug,
      body,
      imageURL,
      createdAt: now,
      updatedAt: now,
      createdBy,
      published
    };
    
    const docRef = await addDoc(collection(db, "newsarticles"), newArticle);
    return docRef.id;
  } catch (error) {
    console.error("Error creating news article:", error);
    throw error;
  }
};

export const updateNewsArticle = async (
  articleId: string,
  data: Partial<NewsArticle>,
  imageFile?: File | null
): Promise<void> => {
  try {
    let updateData = { ...data, updatedAt: Date.now() };
    
    // If title is being updated, regenerate the slug
    if (data.title) {
      const existingArticles = await getAllNewsArticles();
      const currentArticle = existingArticles.find(article => article.id === articleId);
      const existingSlugs = existingArticles
        .filter(article => article.id !== articleId) // Exclude current article
        .map(article => article.slug || '');
      
      const newSlug = generateUniqueSlug(data.title, existingSlugs);
      updateData.slug = newSlug;
    }
    
    // Upload and update image if provided
    if (imageFile) {
      const storageRef = ref(storage, `news-images/${Date.now()}_${imageFile.name}`);
      await uploadBytes(storageRef, imageFile);
      const imageURL = await getDownloadURL(storageRef);
      updateData.imageURL = imageURL;
    }
    
    await updateDoc(doc(db, "newsarticles", articleId), updateData);
  } catch (error) {
    console.error("Error updating news article:", error);
    throw error;
  }
};

export const getNewsArticle = async (articleId: string): Promise<NewsArticle> => {
  try {
    const articleDoc = await getDoc(doc(db, "newsarticles", articleId));
    
    if (articleDoc.exists()) {
      return { id: articleDoc.id, ...articleDoc.data() } as NewsArticle;
    } else {
      throw new Error("News article not found");
    }
  } catch (error) {
    console.error("Error getting news article:", error);
    throw error;
  }
};

export const getNewsArticleBySlug = async (slug: string): Promise<NewsArticle> => {
  try {
    const articlesQuery = query(
      collection(db, "newsarticles"),
      where("slug", "==", slug),
      where("published", "==", true)
    );
    
    const articlesSnapshot = await getDocs(articlesQuery);
    
    if (articlesSnapshot.empty) {
      throw new Error("News article not found");
    }
    
    const articleDoc = articlesSnapshot.docs[0];
    return { id: articleDoc.id, ...articleDoc.data() } as NewsArticle;
  } catch (error) {
    console.error("Error getting news article by slug:", error);
    throw error;
  }
};

export const getAllNewsArticles = async (): Promise<NewsArticle[]> => {
  try {
    const articlesQuery = query(
      collection(db, "newsarticles"),
      orderBy("createdAt", "desc")
    );
    
    const articlesSnapshot = await getDocs(articlesQuery);
    
    const articles: NewsArticle[] = [];
    articlesSnapshot.forEach(doc => {
      if (!doc.data().deleted) {
        articles.push({ id: doc.id, ...doc.data() } as NewsArticle);
      }
    });
    
    return articles;
  } catch (error) {
    console.error("Error getting news articles:", error);
    throw error;
  }
};

export const getPublishedNewsArticles = async (): Promise<NewsArticle[]> => {
  try {
    const articlesQuery = query(
      collection(db, "newsarticles"),
      where("published", "==", true),
      orderBy("createdAt", "desc")
    );
    
    const articlesSnapshot = await getDocs(articlesQuery);
    
    const articles: NewsArticle[] = [];
    articlesSnapshot.forEach(doc => {
      if (!doc.data().deleted) {
        articles.push({ id: doc.id, ...doc.data() } as NewsArticle);
      }
    });
    
    return articles;
  } catch (error) {
    console.error("Error getting published news articles:", error);
    throw error;
  }
};

export const deleteNewsArticle = async (articleId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, "newsarticles", articleId));
  } catch (error) {
    console.error("Error deleting news article:", error);
    throw error;
  }
};

// Event Functions

// Create a new event (admin only)
export const createEvent = async (
  title: string,
  description: string,
  imageFile: File | null,
  date: string,
  time: string,
  location: string,
  locationURL: string,
  price: string,
  status: 'open' | 'closed' | 'hidden',
  createdBy: string
): Promise<string> => {
  try {
    let imageURL = '';
    
    // Upload image if provided
    if (imageFile) {
      const storageRef = ref(storage, `events/${Date.now()}_${imageFile.name}`);
      await uploadBytes(storageRef, imageFile);
      imageURL = await getDownloadURL(storageRef);
    }
    
    const eventData: Omit<Event, 'id'> = {
      title,
      description,
      imageURL,
      date,
      time,
      location,
      locationURL,
      price,
      status,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      createdBy
    };
    
    const docRef = await addDoc(collection(db, "events"), eventData);
    
    // Note: Stripe product creation handled by a separate function to avoid circular imports
    // The stripeProductId will be updated by the createOrUpdateStripeProduct function
    
    return docRef.id;
  } catch (error) {
    console.error("Error creating event:", error);
    throw error;
  }
};

// Update an existing event (admin only)
export const updateEvent = async (
  eventId: string,
  data: Partial<Event>,
  imageFile?: File | null
): Promise<void> => {
  try {
    const eventRef = doc(db, "events", eventId);
    const updateData: any = {
      ...data,
      updatedAt: Date.now()
    };
    
    // Upload new image if provided
    if (imageFile) {
      const storageRef = ref(storage, `events/${Date.now()}_${imageFile.name}`);
      await uploadBytes(storageRef, imageFile);
      updateData.imageURL = await getDownloadURL(storageRef);
    }
    
    await updateDoc(eventRef, updateData);
    
    // Note: Stripe product updates are handled by a separate function to avoid circular imports
    
  } catch (error) {
    console.error("Error updating event:", error);
    throw error;
  }
};

// Get a specific event
export const getEvent = async (eventId: string): Promise<Event> => {
  try {
    const eventDoc = await getDoc(doc(db, "events", eventId));
    if (eventDoc.exists()) {
      return {
        id: eventDoc.id,
        ...eventDoc.data()
      } as Event;
    }
    throw new Error('Event not found');
  } catch (error) {
    console.error("Error getting event:", error);
    throw error;
  }
};

// Get all events (admin)
export const getAllEvents = async (): Promise<Event[]> => {
  try {
    const eventsQuery = query(
      collection(db, "events"),
      orderBy("date", "asc") // Order by date ascending, so upcoming events appear first
    );
    
    const querySnapshot = await getDocs(eventsQuery);
    
    const events = querySnapshot.docs.map((doc: QueryDocumentSnapshot) => ({
      id: doc.id,
      ...doc.data()
    })) as Event[];
    
    return events;
  } catch (error) {
    console.error("Error getting all events:", error);
    throw error;
  }
};

// Get visible events (for users)
export const getVisibleEvents = async (): Promise<Event[]> => {
  try {
    // Use a simpler query that doesn't require a composite index
    const eventsQuery = query(
      collection(db, "events"),
      orderBy("date", "asc") // Order by date ascending
    );
    
    const querySnapshot = await getDocs(eventsQuery);
    
    // Filter for non-hidden events on the client side
    const events = querySnapshot.docs
      .map((doc: QueryDocumentSnapshot) => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter((event: any) => event.status !== 'hidden') as Event[];
    
    return events;
  } catch (error) {
    console.error("Error getting visible events:", error);
    throw error;
  }
};

// Delete an event (admin only)
export const deleteEvent = async (eventId: string): Promise<void> => {
  try {
    const eventRef = doc(db, "events", eventId);
    await deleteDoc(eventRef);
  } catch (error) {
    console.error("Error deleting event:", error);
    throw error;
  }
};

// Function to add a new subscriber to the database
export const addSubscriber = async (email: string): Promise<string> => {
  try {
    // Check if this email is already subscribed
    const subscribersRef = collection(db, "subscribers");
    const q = query(subscribersRef, where("email", "==", email));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      // Email already exists
      return "exists";
    }
    
    // Add new subscriber with timestamp
    const docRef = await addDoc(collection(db, "subscribers"), {
      email,
      subscribedAt: new Date().toISOString(),
      status: "active"
    });
    
    return docRef.id;
  } catch (error) {
    console.error("Error adding subscriber:", error);
    throw error;
  }
};

// Get all subscribers
export const getAllSubscribers = async (): Promise<Array<{id: string, email: string, status: string, subscribedAt: string}>> => {
  try {
    const subscribersRef = collection(db, "subscribers");
    const q = query(subscribersRef, orderBy("subscribedAt", "desc"));
    const querySnapshot = await getDocs(q);
    
    const subscribers = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      email: doc.data().email,
      status: doc.data().status,
      subscribedAt: doc.data().subscribedAt
    }));
    
    return subscribers;
  } catch (error) {
    console.error("Error getting all subscribers:", error);
    throw error;
  }
};

// Get user's ticket purchases
export const getUserTickets = async (userId: string): Promise<Ticket[]> => {
  try {
    const db = getFirestore();
    
    // Query the tickets collection directly where purchaserUID matches the userId
    const ticketsQuery = query(
      collection(db, 'tickets'),
      where('purchaserUID', '==', userId)
    );
    
    const ticketsSnapshot = await getDocs(ticketsQuery);
    const tickets: Ticket[] = [];
    
    ticketsSnapshot.forEach(doc => {
      const data = doc.data();
      
      // Helper function to handle Firebase timestamp conversion
      const handleDate = (dateValue: any): string => {
        if (!dateValue) return new Date().toISOString();
        
        // Firebase timestamp
        if (typeof dateValue === 'object' && 'seconds' in dateValue) {
          return new Date(dateValue.seconds * 1000).toISOString();
        }
        
        // Already a string
        if (typeof dateValue === 'string') {
          return dateValue;
        }
        
        // Default
        return new Date(dateValue).toISOString();
      };
      
      tickets.push({
        id: doc.id,
        name: data.name || 'Unknown',
        email: data.email || 'Unknown',
        company: data.company || '',
        eventId: data.eventId,
        eventTitle: data.eventTitle || 'Unknown Event',
        eventDate: data.eventDate,
        eventTime: data.eventTime,
        eventLocation: data.eventLocation,
        purchaserUID: data.purchaserUID,
        purchaseDate: handleDate(data.purchaseDate),
        paymentId: data.paymentId || doc.id,
        status: data.status || 'active',
        attended: data.attended || false
      });
    });
    
    return tickets;
  } catch (error) {
    console.error('Error getting user tickets:', error);
    throw error;
  }
};

// Get all tickets for a specific event
export const getEventTickets = async (eventId: string): Promise<Ticket[]> => {
  try {
    const db = getFirestore();
    const ticketsQuery = query(
      collection(db, 'tickets'),
      where('eventId', '==', eventId)
    );
    
    const ticketsSnapshot = await getDocs(ticketsQuery);
    const tickets: Ticket[] = [];
    
    ticketsSnapshot.forEach(doc => {
      const data = doc.data();
      tickets.push({
        id: doc.id,
        name: data.name,
        email: data.email,
        company: data.company,
        eventId: data.eventId,
        eventTitle: data.eventTitle,
        eventDate: data.eventDate,
        eventTime: data.eventTime,
        eventLocation: data.eventLocation,
        purchaserUID: data.purchaserUID,
        purchaseDate: data.purchaseDate ? new Date(data.purchaseDate.seconds * 1000).toISOString() : new Date().toISOString(),
        paymentId: data.paymentId || doc.id,
        status: data.status || 'active',
        attended: data.attended || false
      });
    });
    
    return tickets;
  } catch (error) {
    console.error('Error getting event tickets:', error);
    throw error;
  }
};

// Update ticket attendance status
export const updateTicketAttendance = async (ticketId: string, attended: boolean): Promise<void> => {
  try {
    const db = getFirestore();
    const ticketRef = doc(db, 'tickets', ticketId);
    
    await updateDoc(ticketRef, {
      attended,
      // If attended is true, also mark the ticket as used
      ...(attended && { status: 'used' }),
      // If attended is false and the ticket was previously marked as used, set it back to active
      ...(!attended && { status: 'active' })
    });
    
    console.log(`Ticket ${ticketId} attendance updated: ${attended}`);
  } catch (error) {
    console.error('Error updating ticket attendance:', error);
    throw error;
  }
};

// Item Functions

// Get all items (admin)
export const getAllItems = async (): Promise<Item[]> => {
  try {
    const itemsQuery = query(
      collection(db, "items"),
      orderBy("createdAt", "desc")
    );
    
    const querySnapshot = await getDocs(itemsQuery);
    
    const items = querySnapshot.docs.map((doc: QueryDocumentSnapshot) => ({
      id: doc.id,
      ...doc.data()
    })) as Item[];
    
    return items;
  } catch (error) {
    console.error("Error getting all items:", error);
    throw error;
  }
};

// Get visible items (for users)
export const getVisibleItems = async (): Promise<Item[]> => {
  try {
    const itemsQuery = query(
      collection(db, "items"),
      orderBy("createdAt", "desc")
    );
    
    const querySnapshot = await getDocs(itemsQuery);
    
    // Filter for non-hidden items on the client side
    const items = querySnapshot.docs
      .map((doc: QueryDocumentSnapshot) => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter((item: any) => !item.hidden) as Item[];
    
    return items;
  } catch (error) {
    console.error("Error getting visible items:", error);
    throw error;
  }
};

// Create a new item (admin only)
export const createItem = async (
  name: string,
  description: string,
  imageFile: File | null,
  price: string,
  hidden: boolean,
  createdBy: string
): Promise<string> => {
  try {
    let imageURL = '';
    
    // Upload image if provided
    if (imageFile) {
      const storageRef = ref(storage, `items/${Date.now()}_${imageFile.name}`);
      await uploadBytes(storageRef, imageFile);
      imageURL = await getDownloadURL(storageRef);
    }
    
    const itemData: Omit<Item, 'id'> = {
      name,
      description,
      imageURL,
      price,
      hidden,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      createdBy
    };
    
    const docRef = await addDoc(collection(db, "items"), itemData);
    
    return docRef.id;
  } catch (error) {
    console.error("Error creating item:", error);
    throw error;
  }
};

// Update an existing item (admin only)
export const updateItem = async (
  itemId: string,
  data: Partial<Item>,
  imageFile?: File | null
): Promise<void> => {
  try {
    const itemRef = doc(db, "items", itemId);
    const updateData: any = {
      ...data,
      updatedAt: Date.now()
    };
    
    // Upload new image if provided
    if (imageFile) {
      const storageRef = ref(storage, `items/${Date.now()}_${imageFile.name}`);
      await uploadBytes(storageRef, imageFile);
      updateData.imageURL = await getDownloadURL(storageRef);
    }
    
    await updateDoc(itemRef, updateData);
    
  } catch (error) {
    console.error("Error updating item:", error);
    throw error;
  }
};

// Delete an existing item
export const deleteItem = async (itemId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, "items", itemId));
  } catch (error) {
    console.error("Error deleting item:", error);
    throw error;
  }
};

// Feedback Functions

// Upload feedback image
export const uploadFeedbackImage = async (
  userId: string,
  file: File
): Promise<string> => {
  try {
    const storageRef = ref(storage, `feedback-images/${userId}/${Date.now()}-${file.name}`);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    console.error("Error uploading feedback image:", error);
    throw error;
  }
};

// Add new feedback
export const addFeedback = async (
  text: string,
  imageUrl: string,
  userId: string
): Promise<string> => {
  try {
    const feedbackRef = collection(db, "feedback");
    const newFeedback = {
      text,
      imageUrl,
      userId,
      createdAt: new Date(),
      response: null,
      respondedAt: null,
      respondedBy: null,
    };
    
    const docRef = await addDoc(feedbackRef, newFeedback);
    return docRef.id;
  } catch (error) {
    console.error("Error adding feedback:", error);
    throw error;
  }
};

// Get all feedback items
export const getFeedbackItems = async (): Promise<any[]> => {
  try {
    const feedbackRef = collection(db, "feedback");
    const q = query(feedbackRef, orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Error getting feedback items:", error);
    throw error;
  }
};

// Add admin response to feedback
export const addFeedbackResponse = async (
  feedbackId: string,
  response: string,
  adminId: string
): Promise<void> => {
  try {
    const feedbackRef = doc(db, "feedback", feedbackId);
    await updateDoc(feedbackRef, {
      response,
      respondedAt: new Date(),
      respondedBy: adminId
    });
  } catch (error) {
    console.error("Error adding feedback response:", error);
    throw error;
  }
};

// Password Management Functions

// Set password for users who signed up with magic link
export const setUserPassword = async (newPassword: string): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("No user is currently signed in");
    }
    
    await updatePassword(user, newPassword);
  } catch (error) {
    console.error("Error setting password:", error);
    throw error;
  }
};

// Update password (requires current password for reauthentication)
export const updateUserPassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user || !user.email) {
      throw new Error("No user is currently signed in");
    }
    
    // Reauthenticate user with current password
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    
    // Update to new password
    await updatePassword(user, newPassword);
  } catch (error) {
    console.error("Error updating password:", error);
    throw error;
  }
};

// Send password reset email
export const sendPasswordReset = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error("Error sending password reset email:", error);
    throw error;
  }
};

// Check if user has a password (by checking sign-in methods)
export const userHasPassword = async (): Promise<boolean> => {
  try {
    const user = auth.currentUser;
    if (!user || !user.email) {
      return false;
    }
    
    // Check if user has password provider
    const providers = user.providerData.map(provider => provider.providerId);
    return providers.includes('password');
  } catch (error) {
    console.error("Error checking if user has password:", error);
    return false;
  }
};
